import { useAsync } from 'react-use';

import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Hook that resolves the user's group memberships from the Backstage catalog
 * and returns enriched group entities for supervisory org selection.
 */
export const useSuporgId = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { value: enrichedTeamTree = [] } = useAsync(async () => {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    const userEntity = await catalogApi.getEntityByRef(userEntityRef);

    if (!userEntity) return [];

    // Extract group refs from memberOf relations (equivalent to the DB query:
    // SELECT * FROM relations WHERE type='memberOf' AND target_entity_ref LIKE 'group:%')
    const groupRefs = (userEntity.relations ?? [])
      .filter(r => r.type === 'memberOf' && r.targetRef.startsWith('group:'))
      .map(r => r.targetRef);

    if (!groupRefs.length) return [];

    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: groupRefs,
      fields: [
        'metadata.name',
        'metadata.title',
        'metadata.description',
        'spec.type',
      ],
    });

    return groupRefs
      .map((ref, index) => {
        const entity = items[index];
        if (!entity || (entity.spec?.type as string) !== 'supervisory-org')
          return null;
        const name = entity.metadata.name;
        const title = entity.metadata.title;
        return {
          value: ref,
          label: title ? `${title} (${name})` : name,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [identityApi, catalogApi]);

  return { suporgs: enrichedTeamTree };
};
